import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import ReportIcon from "@mui/icons-material/Report";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { BlogBreadcrumbs } from "../components/BlogBreadcrumbs";
import { CommentStatusChip } from "../components/CommentStatusChip";
import { AdminTable, ActionCell } from "../../../common/adminTable";
import { useConfirm } from "../../../common/confirmDialog/useConfirm";
import { fetchComments, updateCommentStatus, deleteComment } from "../commentSlice";

const TABS = ["all", "pending", "approved", "spam", "rejected"];
const rowsPerPage = 20;

export default function CommentList() {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const { comments, total, page, loading, error } = useSelector((state) => state.comment);
  const [tabIdx, setTabIdx] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const statusParam = TABS[tabIdx] === "all" ? undefined : TABS[tabIdx];

  useEffect(() => {
    dispatch(fetchComments({ page: 1, limit: rowsPerPage, status: statusParam }));
  }, [tabIdx]);

  const handleStatusUpdate = async (id, status) => {
    setUpdatingId(id);
    await dispatch(updateCommentStatus({ id, status }));
    setUpdatingId(null);
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Delete Comment",
      description: "This action cannot be undone.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setDeletingId(id);
    await dispatch(deleteComment(id));
    setDeletingId(null);
  };

  const authorName = (c) => {
    if (c.author) {
      const u = c.author;
      return u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.username || "User";
    }
    return c.guestName || "Guest";
  };

  const columns = [
    {
      field: "post",
      headerName: "Post",
      minWidth: 120,
      cellSx: { maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
      renderCell: (c) => c.post?.title || "—",
    },
    {
      field: "author",
      headerName: "Author",
      width: 140,
      renderCell: (c) => authorName(c),
    },
    {
      field: "body",
      headerName: "Body",
      minWidth: 200,
      renderCell: (c) => (
        <Typography variant="body2" noWrap sx={{ fontFamily: "Heebo", color: "#362B24", maxWidth: 260 }}>
          {c.body?.slice(0, 90)}{c.body?.length > 90 ? "..." : ""}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (c) => <CommentStatusChip status={c.status} />,
    },
    {
      field: "createdAt",
      headerName: "Submitted",
      width: 120,
      cellSx: { whiteSpace: "nowrap" },
      renderCell: (c) => dayjs(c.createdAt).format("MMM D, YYYY"),
    },
    {
      field: "__actions",
      headerName: "",
      width: 150,
      align: "right",
      renderCell: (c) => {
        const busy = updatingId === c._id;
        const deleting = deletingId === c._id;
        return (
          <ActionCell
            actions={[
              {
                label: c.status === "approved" ? "Already approved" : "Approve",
                icon: <CheckCircleIcon fontSize="small" />,
                color: "#065F46",
                onClick: () => handleStatusUpdate(c._id, "approved"),
                disabled: c.status === "approved" || busy || deleting,
                loading: busy && c.status !== "approved",
              },
              {
                label: c.status === "rejected" ? "Already rejected" : "Reject",
                icon: <CancelIcon fontSize="small" />,
                color: "#362B24",
                onClick: () => handleStatusUpdate(c._id, "rejected"),
                disabled: c.status === "rejected" || busy || deleting,
                loading: busy && c.status !== "rejected",
                sx: { opacity: c.status === "rejected" ? 0.3 : 0.6 },
              },
              {
                label: c.status === "spam" ? "Already marked spam" : "Mark Spam",
                icon: <ReportIcon fontSize="small" />,
                color: "#f59e0b",
                onClick: () => handleStatusUpdate(c._id, "spam"),
                disabled: c.status === "spam" || busy || deleting,
                loading: busy && c.status !== "spam",
              },
              {
                label: "Delete",
                icon: <DeleteIcon fontSize="small" />,
                color: "#EA001B",
                onClick: () => handleDelete(c._id),
                disabled: busy,
                loading: deleting,
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <BlogBreadcrumbs items={[{ label: "Blog", href: "/blog" }, { label: "Comments" }]} />
      <Typography variant="h5" sx={{ fontWeight: 700, color: "#362B24", fontFamily: "Heebo", mb: 2 }}>
        Comments
      </Typography>

      <Tabs
        value={tabIdx}
        onChange={(_, v) => setTabIdx(v)}
        sx={{
          mb: 2,
          "& .MuiTabs-indicator": { bgcolor: "#065F46" },
          "& .MuiTab-root": { fontFamily: "Heebo", color: "#362B24", opacity: 0.6, textTransform: "none" },
          "& .Mui-selected": { color: "#065F46 !important", opacity: 1 },
        }}
      >
        {TABS.map((s) => (
          <Tab key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} />
        ))}
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <AdminTable
        columns={columns}
        rows={comments}
        loading={loading}
        rowKey="_id"
        emptyText="No comments found"
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={total}
        onPageChange={(pg) => dispatch(fetchComments({ page: pg, limit: rowsPerPage, status: statusParam }))}
        rowSx={(row) =>
          updatingId === row._id
            ? { opacity: 0.5, pointerEvents: "none", transition: "opacity 0.2s" }
            : {}
        }
        deletingId={deletingId}
      />
    </Box>
  );
}
